import React, { useState } from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Link,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import BatchImportResultModal from './BatchImportResultModal';
import UnitBatchImportResultModal from './UnitBatchImportResultModal';
import ImportStatusModal from './ImportStatusModal';
import UnitImportStatusModal from './UnitImportStatusModal';
import FileUploader from '@/components/shared/FileUploader';
import $api from '@/api';
import { Download } from '@mui/icons-material';

interface ConstraintError {
  property?: string;
  constraints?: Record<string, string>;
}

interface RecordEntry {
  valid: boolean;
  row: number;
  rawData: Record<string, any>;
  errors: (ConstraintError | string)[];
}

interface UploadResult {
  originalName: string;
  allValid: boolean;
  totalRecords: number;
  validCount: number;
  invalidCount: number;
  records: RecordEntry[];
  dataFileName: string;
}

interface ImportError {
  record: string;
  errors: string[];
}

interface ImportStatusResult {
  message: string;
  imported: number;
  total: number;
  file: string;
  importType: string;
  errors?: ImportError[];
}

const BatchImportForm: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [importStatusResult, setImportStatusResult] = useState<ImportStatusResult | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [templateFormat, setTemplateFormat] = useState('xlsx');
  const [importType, setImportType] = useState<'property' | 'unit' | null>(null);

  const handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormat: string | null,
  ) => {
    if (newFormat) {
      setTemplateFormat(newFormat);
    }
  };

  const handleFilesSelect = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setLoading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append('file', file));

    try {
      const response = await $api.post<UploadResult>('/batch-import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = response.data;
      if (result.originalName.includes('property_import')) {
        setImportType('property');
      } else if (result.originalName.includes('unit_import')) {
        setImportType('unit');
      } else {
        setImportType(null);
      }
      setUploadResult(result);
      setShowResultModal(true);
    } catch (error) {
      alert('Error uploading file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!uploadResult) return;

    try {
      const response = await $api.post<ImportStatusResult>('/batch-import/confirm', {
        dataFileName: uploadResult.dataFileName,
      });
      setImportStatusResult(response.data);
      setShowResultModal(false);
      setShowStatusModal(true);
    } catch (error) {
      alert('Error confirming import');
    }
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setImportStatusResult(null);
    setUploadResult(null);
    setFiles([]);
    setImportType(null);
  };

  const host = process.env.REACT_APP_BACKEND_HOST || 'localhost';
  const port = process.env.REACT_APP_BACKEND_PORT;

  let backendUrl = host.includes('://') ? host : `http://${host}`;
  if (port) {
    backendUrl = `${backendUrl}:${port}`;
  }

  const renderStatusModal = () => {
    if (!importStatusResult) return null;

    if (importStatusResult.importType === 'unit') {
      return (
        <UnitImportStatusModal
          open={showStatusModal}
          onClose={handleCloseStatusModal}
          result={importStatusResult}
        />
      );
    }

    return (
      <ImportStatusModal
        open={showStatusModal}
        onClose={handleCloseStatusModal}
        result={importStatusResult}
      />
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Step 1: Download and fill in the templates
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Download the required template and fill it in with data according to the fields.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 0, mr: 2, flexShrink: 0 }}>Template format:</Typography>
            <ToggleButtonGroup
              color="primary"
              value={templateFormat}
              exclusive
              onChange={handleFormatChange}
              aria-label="Template Format"
            >
              <ToggleButton value="xlsx">XLSX</ToggleButton>
              <ToggleButton value="csv">CSV</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Download color="primary" />
                  <Link
                    href={`${backendUrl}/templates/property_import.${templateFormat}`}
                    download
                    underline="hover"
                  >
                    Property template
                  </Link>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Download color="primary" />
                  <Link
                    href={`${backendUrl}/templates/unit_import.${templateFormat}`}
                    download
                    underline="hover"
                  >
                    Unit template
                  </Link>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
          <Typography variant="body1" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
            Attention!!! Do not change the file names or structure. Just fill in your data as is.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Step 2: Upload the filled files
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            After you have filled in the templates, upload them in the form below in order: first the property template, then the unit template.
          </Typography>
          <FileUploader
            onFilesSelect={handleFilesSelect}
            files={files}
            acceptFiles={['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
            filesLimit={1}
            maxFileSize={50000000} // 50 МБ
            filesDescription="XLS, CSV / max. 50 MB"
            useToasts={true}
            toastDuration={2000} // 3 секунды
            replaceFilesOnAdd={true}
            onError={(errorMsg) => console.log('File upload error:', errorMsg)}
          />
          <Box mt={2}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={files.length === 0 || loading}
              sx={{ width: '100%' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {uploadResult && importType === 'property' && (
        <BatchImportResultModal
          open={showResultModal}
          onClose={handleCloseResultModal}
          onConfirm={handleConfirmImport}
          result={uploadResult}
        />
      )}

      {uploadResult && importType === 'unit' && (
        <UnitBatchImportResultModal
          open={showResultModal}
          onClose={handleCloseResultModal}
          onConfirm={handleConfirmImport}
          result={uploadResult}
        />
      )}

      {renderStatusModal()}
    </Box>
  );
};

export default BatchImportForm;
